import Icon from '@/components/ui/AppIcon';

interface PortalFeature {
  id: number;
  icon: string;
  title: string;
  description: string;
}

const portalFeatures: PortalFeature[] = [
  {
    id: 1,
    icon: "CloudArrowUpIcon",
    title: "Easy Upload",
    description: "Upload your audio content in minutes with our intuitive dashboard"
  },
  {
    id: 2,
    icon: "ChartBarIcon",
    title: "Analytics Dashboard",
    description: "Track your performance with detailed insights and listener demographics"
  },
  {
    id: 3,
    icon: "UserGroupIcon",
    title: "Audience Management",
    description: "Engage with your followers through comments, polls, and live sessions"
  },
  {
    id: 4,
    icon: "CurrencyRupeeIcon",
    title: "Revenue Tracking",
    description: "Monitor your earnings in real-time with transparent reporting"
  },
  {
    id: 5,
    icon: "AcademicCapIcon",
    title: "Creator Academy",
    description: "Access free courses on content creation, marketing, and monetization"
  },
  {
    id: 6,
    icon: "ShieldCheckIcon",
    title: "Content Protection",
    description: "Your content is protected with advanced copyright and piracy prevention"
  }
];

interface Requirement {
  id: number;
  text: string;
  icon: string;
}

const requirements: Requirement[] = [
  {
    id: 1,
    text: "Original audio content (stories, podcasts, narrations)",
    icon: "MicrophoneIcon"
  },
  {
    id: 2,
    text: "Minimum 3 episodes or 30 minutes of content",
    icon: "ClockIcon"
  },
  {
    id: 3,
    text: "Good audio quality (clear voice, minimal background noise)",
    icon: "MusicalNoteIcon"
  },
  {
    id: 4,
    text: "Consistent upload schedule commitment",
    icon: "CalendarIcon"
  }
];

export default function CreatorPortalSection() {
  return (
    <section className="py-16 bg-gradient-to-br from-accent/5 to-primary/5">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full mb-4">
            <Icon name="RocketLaunchIcon" size={20} className="text-primary" />
            <span className="text-sm font-semibold text-primary">Creator Portal</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-text-primary mb-4">
            Everything You Need to Succeed
          </h2>
          <p className="text-lg text-text-secondary max-w-2xl mx-auto">
            Professional tools and resources designed to help you create, grow, and monetize your content
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {portalFeatures.map((feature) => (
            <div
              key={feature.id}
              className="bg-card rounded-xl p-6 shadow-warm hover:shadow-warm-lg transition-all duration-300 hover:-translate-y-1"
            >
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                <Icon name={feature.icon as any} size={24} className="text-primary" />
              </div>
              <h3 className="text-lg font-heading font-bold text-text-primary mb-2">
                {feature.title}
              </h3>
              <p className="text-text-secondary text-sm leading-relaxed">
                {feature.description}
              </p>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="bg-card rounded-2xl p-8 shadow-warm">
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="CheckCircleIcon" size={24} className="text-success" />
              </div>
              <h3 className="text-2xl font-heading font-bold text-text-primary">
                Getting Started Requirements
              </h3>
            </div>

            <div className="space-y-4">
              {requirements.map((req) => (
                <div key={req.id} className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <Icon name={req.icon as any} size={16} className="text-primary" />
                  </div>
                  <p className="text-text-secondary leading-relaxed">{req.text}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 p-4 bg-accent/10 rounded-lg border-2 border-accent">
              <div className="flex items-start space-x-3">
                <Icon name="LightBulbIcon" size={20} className="text-accent flex-shrink-0 mt-1" />
                <p className="text-sm text-text-secondary">
                  <span className="font-bold text-text-primary">Pro Tip:</span> Start with content you're passionate about. Authenticity resonates with listeners more than perfect production quality.
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-primary to-primary-dark rounded-2xl p-8 text-primary-foreground shadow-warm-lg">
            <div className="mb-6">
              <Icon name="SparklesIcon" size={32} className="mb-4" />
              <h3 className="text-2xl font-heading font-bold mb-2">
                Ready to Start Your Journey?
              </h3>
              <p className="opacity-90 leading-relaxed">
                Join thousands of creators who are already earning while sharing their stories. The application process takes less than 5 minutes.
              </p>
            </div>

            <div className="space-y-4 mb-8">
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary-foreground/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold">1</span>
                </div>
                <span>Fill out the creator application form</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary-foreground/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold">2</span>
                </div>
                <span>Upload your sample content portfolio</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary-foreground/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold">3</span>
                </div>
                <span>Get approved within 48 hours</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-8 h-8 bg-primary-foreground/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="font-bold">4</span>
                </div>
                <span>Start uploading and earning immediately</span>
              </div>
            </div>

            <button className="w-full flex items-center justify-center space-x-2 px-6 py-4 bg-primary-foreground text-primary rounded-lg font-cta font-bold transition-all duration-300 hover:bg-opacity-90 hover:shadow-warm">
              <Icon name="PencilSquareIcon" size={20} />
              <span>Apply Now - It's Free</span>
            </button>

            <p className="text-center text-sm opacity-75 mt-4">
              No application fees • No hidden charges • Cancel anytime
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}